import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock3, Lock, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/common.utils";
import { useBillingGate } from "@/hooks/useBillingGate";

/**
 * Billing state that needs to follow the user around the app.
 *
 * Two distinct states, deliberately different in tone and in behaviour:
 *
 *  - **Ending soon** (trial, ≤3 days): a reminder. Dismissible for the session,
 *    because nothing is broken yet and repeating it on every page would nag.
 *  - **Ended** (past_due / canceled): a blocked state, so it is NOT dismissible.
 *    The server is refusing new deals and invitations; hiding that would just
 *    move the surprise to the next button press. It leads with what still
 *    works, because the first thing someone fears when billing lapses is that
 *    their data is gone — and here it genuinely isn't, since existing deals
 *    stay fully editable.
 *
 * Internal users only — externals have no subscription to act on.
 */
export default function TrialBanner() {
  const { blocked, needsCard, endingSoon, daysLeft, endedOn, endsOn, blockedReason, billingHref } = useBillingGate();
  const [dismissed, setDismissed] = useState(false);

  // Keyed by the day count so a fresh dismissal is required as the deadline
  // moves — dismissing "3 days left" shouldn't also silence "ends today".
  const dismissKey = `stoop_trial_banner_dismissed_${daysLeft}`;

  useEffect(() => {
    if (!endingSoon) return;
    try {
      if (sessionStorage.getItem(dismissKey)) setDismissed(true);
    } catch {
      // Private browsing can throw on sessionStorage; showing the banner is the
      // safe failure here.
    }
  }, [endingSoon, dismissKey]);

  // The billing page carries its own "start your trial" block.
  if (!billingHref || needsCard) return null;
  if (!blocked && (!endingSoon || dismissed)) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(dismissKey, "1");
    } catch {
      /* non-fatal — the banner simply reappears next navigation */
    }
    setDismissed(true);
  };

  const title = blocked
    ? endedOn ? `Your trial ended on ${endedOn}.` : "Your trial has ended."
    : daysLeft === 0
      ? "Your trial ends today."
      : daysLeft === 1
        ? "Your trial ends tomorrow."
        : `${daysLeft} days left in your trial.`;

  const description = blocked
    ? `${blockedReason} Your existing deals keep working — you and your clients can still update tasks, upload documents and talk to each other.`
    : endsOn
      ? `Your trial runs through ${endsOn}. Upgrade to keep starting new deals and sending invitations.`
      : "Upgrade to keep starting new deals and sending invitations.";

  const Icon = blocked ? Lock : Clock3;

  return (
    <Alert
      variant={blocked ? "destructive" : "warning"}
      className={cn(
        "rounded-none border-x-0 border-t-0 px-0 py-0",
        blocked && "border-dash-rust/30 bg-dash-rust/10",
      )}
      data-testid={blocked ? "billing-blocked-banner" : "trial-banner"}
    >
      <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center sm:px-6 lg:px-8">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-dash-rust/10 text-dash-rust">
            <Icon className="size-4" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <AlertTitle className="mb-0.5 text-sm font-semibold leading-5 text-dash-ink sm:text-base">
              {title}
            </AlertTitle>
            <AlertDescription className="text-xs leading-5 text-dash-muted sm:text-sm">
              {description}
            </AlertDescription>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 pl-12 sm:pl-0">
          <Button variant="dashboardPrimary" size="sm" asChild>
            <Link to={billingHref} data-testid="trial-banner-cta">
              {blocked ? "Reopen my studio" : "Keep my studio open"}
              <ArrowRight data-icon="inline-end" aria-hidden="true" />
            </Link>
          </Button>
          {/* No dismiss on the blocked state — see the note at the top. */}
          {!blocked && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={dismiss}
              className="size-8 shrink-0 rounded-full text-dash-muted hover:text-dash-ink"
              aria-label="Dismiss trial reminder"
              title="Dismiss trial reminder"
              data-testid="trial-banner-dismiss"
            >
              <X aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>
    </Alert>
  );
}
